import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DSBox from '../../../design-system/DSBox';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import DSTabGradientViewPaper from '../../../design-system/Paper/DSTabGradientViewPaper';
import { DSCalendar } from '../../../design-system/DSCalendar';
import CalendarToolbar from './CalendarToolbar';
import CalendarEventListView from './CalendarEventListView';
import useCalendarEventsQuery from '../hooks/useCalendarEventsQuery';
import { useCalendarNavigation } from '../hooks/useCalendarNavigation';
import { useCountrySelection } from '../hooks/useCountrySelection';
import { KLYDO_YELLOW } from '../../../consts/colors';
import { generateObjectId } from '../../../utils';
import { CalendarViewType } from '../../../utils/calendarEvents';
import {
  DSTitleTypography,
  DSTypography,
} from '../../../design-system/Typography';
import useDeviceType from '../../../hooks/useDeviceType';

const CalendarEvents = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  const { currentDate, handleNavigate } = useCalendarNavigation();
  const currentYear = currentDate.getFullYear();

  const {
    selectedCountry,
    selectedState,
    handleCountrySelect,
    handleStateSelect,
  } = useCountrySelection();

  const {
    data: calendarEvents,
    isLoading,
    error,
  } = useCalendarEventsQuery({
    country: selectedCountry,
    year: currentYear,
    state: selectedState,
  });

  const [currentView, setCurrentView] = useState<CalendarViewType>(
    CalendarViewType.LIST,
  );

  const handleViewChange = (view: CalendarViewType) => {
    setCurrentView(view);
  };

  const handlePlusClick = (calendarEventId?: string) => {
    const createdObjectId = generateObjectId();
    navigate(`/klydo/${createdObjectId}`, {
      state: {
        ...(calendarEventId && { calendarEventId }),
      },
    });
  };
  if (isMobile) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '87vh',
          padding: spacing(3),
        }}
      >
        <DSTitleTypography color="error" sx={{ textAlign: 'center' }}>
          For the best experience please use a tablet or desktop.
        </DSTitleTypography>
      </DSBox>
    );
  }

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '87vh',
          backgroundColor: 'transparent',
          borderRadius: spacing(2),
        }}
      >
        <DSCircularProgress sx={{ color: KLYDO_YELLOW }} />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox
        sx={{
          padding: spacing(2),
          backgroundColor: 'transparent',
          borderRadius: spacing(2),
        }}
      >
        <DSTypography
          sx={{
            color: palette.text.secondary,
            textAlign: 'center',
            fontFamily: 'Gilroy-Medium',
            fontSize: spacing(2),
          }}
        >
          {'An error occurred while fetching calendar events.'}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSTabGradientViewPaper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginY: spacing(3),
        minHeight: 'fit-content',
        height: 'auto',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          paddingY: spacing(3),
        }}
      >
        <DSTypography>
          Our Events Calendar highlights upcoming holidays, observances, and
          celebrations around the world. Submit Klydos that match these moments,
          and the best fits will be selected to be featured right when people
          are expecting them.
        </DSTypography>
      </DSBox>

      <CalendarToolbar
        label={dayjs(currentDate).format('MMMM YYYY')}
        currentDate={currentDate}
        onNavigate={handleNavigate}
        selectedCountry={selectedCountry}
        selectedState={selectedState}
        onCountrySelect={handleCountrySelect}
        onStateSelect={handleStateSelect}
        currentView={currentView}
        onViewChange={handleViewChange}
        disabled={
          !calendarEvents || calendarEvents.length === 0 || isLoading || !!error
        }
      />
      {currentView === CalendarViewType.GRID ? (
        <DSCalendar
          currentDate={currentDate}
          calendarEvents={calendarEvents || []}
          onPlusClick={handlePlusClick}
        />
      ) : (
        <CalendarEventListView calendarEvents={calendarEvents || []} />
      )}
    </DSTabGradientViewPaper>
  );
};

export default CalendarEvents;
